import {TextArea} from 'design-react-kit'
import styled from 'styled-components'
import React from 'react'
import InputGroup, {InputGroupProps} from './InputGroup'
import {isEmptyValue} from 'src/utilities/utility'

const TextAreaStyled = styled.div`
  .textarea-group > .input-group {
    display: block;
  }

  .form-group {
    width: 100%;
    min-width: 0;
  }

  .textarea-group .form-group {
    display: flex;
    flex-direction: column;
    margin: 0;
  }

  .textarea-group label.active {
    position: static;
    transform: none;
    line-height: 1.5;
    padding: 0;
    margin: 0.5rem 0;
    overflow-wrap: anywhere;
    order: ${(props) => (props.$labelBottom ? 1 : -1)};
  }

  textarea {
    box-sizing: border-box;
    display: block;
    width: 100%;
    max-width: 100%;
    min-height: 8rem;
    height: auto;
    resize: vertical;
    overflow: auto;
  }

  textarea[readonly] {
    background-color: #e9ecef;
    cursor: text;
  }

  textarea.is-invalid {
    border-color: #a31f33 !important;
  }

`

class GenericTextArea extends React.Component<GenericTextAreaProps, any> {
  isMount = false
  textareaRef = React.createRef<HTMLTextAreaElement>()
  resizeObserver?: ResizeObserver
  observedWidth = 0

  fitContent = () => {
    const textarea = this.textareaRef.current
    if (!textarea || textarea.clientWidth === 0) return

    // Reset first so deleting text can shrink the field as well.
    textarea.style.height = 'auto'
    const style = window.getComputedStyle(textarea)
    const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
    textarea.style.height = `${textarea.scrollHeight + borders}px`
  }

  onChange = (e) => {
    if (this.props.onChange) {
      this.props.onChange(this.props.id, e.target.value)
    }
  }

  componentDidMount() {
    this.isMount = true
    this.fitContent()
    if (typeof ResizeObserver !== 'undefined' && this.textareaRef.current) {
      this.resizeObserver = new ResizeObserver(([entry]) => {
        const width = entry.contentRect.width
        if (width !== this.observedWidth) {
          this.observedWidth = width
          this.fitContent()
        }
      })
      this.resizeObserver.observe(this.textareaRef.current)
    }
    document.fonts?.ready.then(() => {
      if (this.isMount) this.fitContent()
    })
    if (this.props.onInit) {
      this.props.onInit(this)
    }
  }

  componentDidUpdate(prevProps: GenericTextAreaProps) {
    if (prevProps.value !== this.props.value || prevProps.rows !== this.props.rows ||
        prevProps.isHidden !== this.props.isHidden) {
      this.fitContent()
    }
  }

  componentWillUnmount() {
    this.isMount = false
    this.resizeObserver?.disconnect()
  }

  render() {
    const rows = this.props.rows ? this.props.rows : 5
    const isInvalid = this.props.invalid || !isEmptyValue(this.props.errorMessage)
    return (
      <TextAreaStyled className={this.props.className} $labelBottom={this.props.labelBottom}>
        <InputGroup {...this.props} rows={rows} type={'textarea'} label={''}>
          <TextArea
            innerRef={this.textareaRef}
            hidden={this.props.isHidden}
            defaultValue={this.props.defaultValue}
            placeholder={this.props.placeholder}
            id={this.props.id}
            name={this.props.name}
            className={this.props.className}
            aria-disabled={this.props.isDisabled || undefined}
            label={this.props.label}
            onChange={this.onChange}
            normalized={this.props.normalized}
            readOnly={this.props.isReadOnly || this.props.isDisabled}
            invalid={isInvalid}
            value={this.props.value || ''}
            rows={rows}
          />
        </InputGroup>
      </TextAreaStyled>
    )
  }
}

export declare interface GenericTextAreaProps extends InputGroupProps {
  value?: any
  defaultValue?: any
  onChange?: any
  normalized?: any
  isReadOnly?: boolean
  invalid?: boolean
  onInit?: any
  isDisabled?: boolean
  labelBottom?: boolean
}

export default GenericTextArea
